"use client";

import { useState } from "react";
import useSWR from "swr";
import { Hammer, Calendar, Loader2, ShoppingBag } from "lucide-react";
import { formatEtaRange } from "@/lib/build-lifecycle";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/context/ToastContext";

const fetcher = (u: string) => fetch(u).then((r) => r.json());

interface Build {
  id: string;
  barcodeId: string;
  name: string;
  photoUrl: string | null;
  estimatedReadyAt: string | null;
  currentStage: { customerLabel: string; customerVisible: boolean } | null;
}

/**
 * "Or pre-order one being built" panel on the PDP. Renders one card
 * per in-production WigAsset admin has listed for sale. Reserve button
 * adds the SPECIFIC physical wig to the cart (CartItemData.wigAssetId).
 * /api/checkout atomically claims the asset by setting
 * WigAsset.orderItemId at order creation; concurrent reservations on
 * the same wig fail-fast with a friendly error.
 */
export default function InProductionBuilds({
  productId,
  productName,
  productPrice,
  productImage,
}: {
  productId: string;
  productName: string;
  productPrice?: number;
  productImage?: string;
}) {
  const { data, mutate } = useSWR<{ builds: Build[] }>(
    `/api/products/${productId}/in-production`,
    fetcher,
    { refreshInterval: 60_000 }
  );
  const { addItem, items, openCart } = useCart();
  const { toast } = useToast() as {
    toast: (m: string, t?: "success" | "error" | "info") => void;
  };
  const builds = data?.builds ?? [];

  if (builds.length === 0) return null;

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-5 mt-6">
      <div className="flex items-center gap-2 text-amber-900 mb-1">
        <Hammer className="h-4 w-4" />
        <h3 className="text-sm font-medium uppercase tracking-wide">
          Or pre-order one being built
        </h3>
      </div>
      <p className="text-xs text-amber-800/80 mb-4">
        These exact wigs are currently in production. Reserve one now — pay 50%
        deposit today, balance when it&apos;s ready.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {builds.map((b) => (
          <BuildCard
            key={b.id}
            build={b}
            productId={productId}
            productName={productName}
            productPrice={productPrice ?? 0}
            productImage={productImage ?? b.photoUrl ?? undefined}
            inCart={items.some((i) => i.wigAssetId === b.id)}
            onReserved={() => {
              openCart();
              mutate();
            }}
            onSnipe={() => {
              toast("That wig was just reserved by someone else", "info");
              mutate();
            }}
            onError={(msg) => toast(msg, "error")}
            addItem={addItem}
          />
        ))}
      </div>
    </div>
  );
}

function BuildCard({
  build,
  productId,
  productName,
  productPrice,
  productImage,
  inCart,
  onReserved,
  onSnipe,
  onError,
  addItem,
}: {
  build: Build;
  productId: string;
  productName: string;
  productPrice: number;
  productImage?: string;
  inCart: boolean;
  onReserved: () => void;
  onSnipe: () => void;
  onError: (msg: string) => void;
  addItem: ReturnType<typeof useCart>["addItem"];
}) {
  const [busy, setBusy] = useState(false);
  async function reserve() {
    if (busy || inCart) return;
    setBusy(true);
    try {
      // Server-side soft-hold — also catches "another customer is in checkout
      // on this wig" before we add to local cart. Logged-in users only at
      // this layer; guest carts still pass wigAssetId through to /api/checkout.
      const res = await fetch("/api/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId, wigAssetId: build.id, quantity: 1 }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        if (res.status === 409) {
          onSnipe();
          return;
        }
        if (res.status === 401) {
          // Guest — fall through to local cart only. /api/checkout will
          // re-validate the wigAssetId server-side.
        } else {
          onError(j.error ?? "Could not reserve this build");
          return;
        }
      }
      addItem({
        productId,
        name: `${productName} · ${build.barcodeId} (in production)`,
        price: productPrice,
        quantity: 1,
        image: productImage,
        wigAssetId: build.id,
        wigAssetBarcode: build.barcodeId,
      });
      onReserved();
    } catch {
      onError("Network error — try again");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-amber-200 rounded-md">
      <div className="h-16 w-16 rounded bg-stone-50 overflow-hidden flex-shrink-0">
        {build.photoUrl ? (
          <img src={build.photoUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-stone-300">
            <Hammer className="h-5 w-5" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-mono text-stone-500">{build.barcodeId}</div>
        <div className="text-sm text-stone-900 truncate">{build.name}</div>
        <div className="text-xs text-stone-600 mt-0.5 inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {build.currentStage?.customerVisible
            ? `${build.currentStage.customerLabel} · `
            : ""}
          ETA{" "}
          {formatEtaRange(
            build.estimatedReadyAt ? new Date(build.estimatedReadyAt) : null
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={reserve}
        disabled={busy || inCart}
        className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1 flex-shrink-0 ${
          inCart
            ? "bg-emerald-600 text-white"
            : "bg-amber-600 text-white hover:bg-amber-700"
        } disabled:opacity-50`}
      >
        {busy && <Loader2 className="h-3 w-3 animate-spin" />}
        {!busy && (inCart ? <ShoppingBag className="h-3 w-3" /> : null)}
        {inCart ? "In cart" : "Reserve"}
      </button>
    </div>
  );
}
